import Icon from '@/components/Icon'
import PrescriptionSelectionTagList from '@/components/prescriptions/PrescriptionSelectionTagList'
import {
	PrescriptionConfirmationAlert,
	PrescriptionIncompleteAlert,
} from '@/components/prescriptions/PrescriptionStatusAlert'
import PrescriptionWorkSelector from '@/components/prescriptions/PrescriptionWorkSelector'
import {
	CourseSelections,
	courseRequiresForm,
	courseRequiresShakespeare,
	expandSelectorsByQty,
	getCourseConfirmationMessage,
	getCourseOptionGroups,
	getCourseStatus,
	getUnmetMessages,
	resolveCourseSelections,
	resolveCourseWithElective,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import type {
	PrescriptionSelectionCourse,
	PrescriptionSelectionFocusAreaSelector,
} from '@/types/fetchPrescriptionSelection'
import { ChangeEvent, useId, useMemo } from 'react'

export interface PrescriptionElectiveAccordionProps {
	course: PrescriptionSelectionCourse
	courseSelections: CourseSelections | undefined
	electiveOptionIndex: number | undefined
	onElectiveChange: (_newIndex: number | undefined) => void
	onSelectionChange: (_slotIndex: number, _workIds: string[]) => void
	onResetCourse: () => void
}

interface SelectorRow {
	selector: PrescriptionSelectionFocusAreaSelector
	slotIndex: number
}

const ELECTIVE_PLACEHOLDER = 'Select elective'

export function PrescriptionElectiveAccordion({
	course,
	courseSelections,
	electiveOptionIndex,
	onElectiveChange,
	onSelectionChange,
	onResetCourse,
}: PrescriptionElectiveAccordionProps) {
	const reactId = useId()
	const courseTitle = course['course-title']
	const optionGroups = getCourseOptionGroups(course)
	const focusArea = course.selectors?.[0]?.['focus-area-title'] ?? ''

	const resolvedCourse = useMemo(
		() => resolveCourseWithElective(course, electiveOptionIndex),
		[course, electiveOptionIndex],
	)

	const status = getCourseStatus(
		course,
		courseSelections,
		electiveOptionIndex,
	)
	const unmetMessages = useMemo(
		() => getUnmetMessages(course, courseSelections, electiveOptionIndex),
		[course, courseSelections, electiveOptionIndex],
	)

	const prescriptionRows = useMemo<SelectorRow[]>(() => {
		if (electiveOptionIndex === undefined) return []
		return expandSelectorsByQty(resolvedCourse).map((slot) => ({
			selector: slot.selector,
			slotIndex: slot.flatSlotIndex,
		}))
	}, [resolvedCourse, electiveOptionIndex])

	const selectedEntries = useMemo(
		() => resolveCourseSelections(resolvedCourse, courseSelections),
		[resolvedCourse, courseSelections],
	)
	const hasShakespeareRule = useMemo(
		() => courseRequiresShakespeare(course),
		[course],
	)
	const hasFormRequirement = useMemo(
		() => courseRequiresForm(course),
		[course],
	)

	const handleElectiveSelect = (event: ChangeEvent<HTMLSelectElement>) => {
		const raw = event.target.value
		if (raw === '') {
			onElectiveChange(undefined)
			return
		}
		const parsed = Number(raw)
		if (!Number.isFinite(parsed)) return
		onElectiveChange(parsed)
	}

	const electiveSelectId = `elective-${reactId}`
	const electiveValue =
		electiveOptionIndex === undefined ? '' : String(electiveOptionIndex)

	return (
		<div className="flex flex-col gap-4">
			<div>
				<table className="w-full border-collapse text-left">
					<thead className="hidden md:table-header-group">
						<tr>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04 w-2/5"
							>
								Focus area
							</th>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04"
							>
								Your selection
							</th>
						</tr>
					</thead>
					<tbody>
						<tr className="md:border-b md:border-nsw-grey-04 flex flex-col md:table-row gap-2 md:gap-0 mb-4 md:mb-0">
							<td
								className="p-3 align-top md:w-2/5"
								rowSpan={
									prescriptionRows.length > 0
										? prescriptionRows.length + 1
										: 1
								}
							>
								<span className="font-bold md:font-normal">
									{focusArea}
								</span>
							</td>
							<td className="p-3 align-top">
								<div className="relative w-full">
									<select
										id={electiveSelectId}
										aria-label={`Select elective for ${courseTitle}`}
										className="nsw-form__select w-full !border-nsw-grey-04"
										style={{ backgroundImage: 'none' }}
										value={electiveValue}
										onChange={handleElectiveSelect}
										data-testid={electiveSelectId}
									>
										<option value="">
											{ELECTIVE_PLACEHOLDER}
										</option>
										{optionGroups.map((og, idx) => (
											<option key={idx} value={idx}>
												{og.option}
											</option>
										))}
									</select>
									<Icon
										icon="ic:baseline-keyboard-arrow-down"
										width={24}
										height={24}
										aria-hidden="true"
										className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-nsw-grey-01"
									/>
								</div>
							</td>
						</tr>
						{prescriptionRows.map((row) => {
							const selectedForRow = selectedEntries.filter(
								(entry) => entry.slotIndex === row.slotIndex,
							)
							const htmlId = `selector-${reactId}-${row.slotIndex}`
							return (
								<tr
									key={row.slotIndex}
									className="md:border-b md:border-nsw-grey-04 flex flex-col md:table-row gap-2 md:gap-0 mb-4 md:mb-0"
								>
									<td className="p-3 align-top">
										<PrescriptionWorkSelector
											course={resolvedCourse}
											selector={row.selector}
											slotIndex={row.slotIndex}
											courseSelections={courseSelections}
											onChange={(workIds) =>
												onSelectionChange(
													row.slotIndex,
													workIds,
												)
											}
											htmlId={htmlId}
											ariaLabel={`Select work for ${focusArea}`}
										/>
										{selectedForRow.length > 0 && (
											<div
												className="mt-3 flex flex-wrap"
												data-testid="selection-tag-list"
											>
												{selectedForRow.map(
													(entry, idx) => (
														<PrescriptionSelectionTagList
															key={`${entry.work.id}-${idx}`}
															work={entry.work}
															courseTitle={
																courseTitle
															}
															hasShakespeareRule={
																hasShakespeareRule
															}
															hasFormRequirement={
																hasFormRequirement
															}
														/>
													),
												)}
											</div>
										)}
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>

			{status !== 'complete' && (
				<PrescriptionIncompleteAlert messages={unmetMessages} />
			)}

			{status === 'complete' && (
				<PrescriptionConfirmationAlert
					message={getCourseConfirmationMessage(course)}
					courseTitle={courseTitle}
				/>
			)}

			<div className="flex justify-end">
				<button
					type="button"
					className="underline font-bold nsw-text--brand-dark"
					onClick={onResetCourse}
					data-testid="prescription-reset-course"
				>
					Reset selections
				</button>
			</div>
		</div>
	)
}

export default PrescriptionElectiveAccordion
